import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Coins, 
  TrendingUp, 
  Lock, 
  Unlock, 
  Gift,
  RefreshCw,
  Info,
  CheckCircle,
  Wallet,
  Shield,
  ExternalLink,
  AlertTriangle,
  Plus,
  History,
  Vote,
  Activity,
  Heart
} from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useWallet } from '@/context/WalletContext';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useKutroContracts } from '@/hooks/useKutroContracts';
import { formatNumber } from '@/lib/utils';

// Token configuration for MetaMask
const KOJIRU_TOKEN = {
  address: process.env.REACT_APP_KOJIRU_TOKEN_ADDRESS || '',
  symbol: 'KOJI',
  decimals: 18,
  image: 'https://raw.githubusercontent.com/kutro/brand/main/logo.png'
};

// Staking tiers
const TIERS = [
  { id: 0, name: 'No Agent', required: 0, benefits: [] },
  { id: 1, name: 'Basic Agent', required: 50, benefits: ['Basic AI credit analysis', 'Market alerts'] },
  { id: 2, name: 'Advanced Agent', required: 200, benefits: ['Advanced credit optimization', 'Auto-bidding suggestions', 'Priority support'] },
  { id: 3, name: 'Pro Agent', required: 500, benefits: ['Full AI negotiation', 'Portfolio management', 'Custom strategies', 'VIP support'] }
];

// Base Mainnet block explorer
const BLOCK_EXPLORER = 'https://basescan.org';

// Staking is live: KojiruStakingV2 verified on BaseScan and bound to the real KOJI token,
// reward pool funded with 2,000,000 KOJI from the treasury (Apr 30, 2026 — tx 0x61b976bd...8f56).
const STAKING_VERIFIED = true;

export default function StakingPage() {
  const { walletAddress, isConnected, connectWallet, isOnBase, switchToBase, chainId } = useWallet();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const {
    loading: contractLoading,
    error: contractError,
    getTokenBalance,
    getStakeInfo,
    getPoolStats,
    stakeTokens,
    unstakeTokens,
    claimRewards,
    checkContractsDeployed
  } = useKutroContracts();
  
  const [loading, setLoading] = useState(false);
  const [stakeAmount, setStakeAmount] = useState('');
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const [txHash, setTxHash] = useState(null);
  
  // Contract state
  const [contractsDeployed, setContractsDeployed] = useState(false);
  const [useMockData, setUseMockData] = useState(true);
  
  // Token/Staking state
  const [tokenBalance, setTokenBalance] = useState(0);
  const [stakedAmount, setStakedAmount] = useState(0);
  const [pendingRewards, setPendingRewards] = useState(0);
  const [currentTier, setCurrentTier] = useState(0);
  const [tierName, setTierName] = useState('No Agent');
  const [poolStats, setPoolStats] = useState({
    totalStaked: 0,
    rewardRate: 10,
    rewardPool: 0
  });
  const [livePoolStats, setLivePoolStats] = useState(null);

  // Fetch live pool stats from backend (works without MetaMask)
  useEffect(() => {
    const fetchLiveStats = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staking/stats`);
        if (res.ok) {
          const data = await res.json();
          setLivePoolStats(data);
          // Update pool stats if using mock data (no MetaMask)
          if (useMockData) {
            setPoolStats({
              totalStaked: parseFloat(data.total_staked),
              rewardRate: data.reward_rate_pct,
              rewardPool: parseFloat(data.reward_pool)
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch live pool stats:', err);
      }
    };
    fetchLiveStats();
    const interval = setInterval(fetchLiveStats, 60000);
    return () => clearInterval(interval);
  }, [useMockData]);

  // Check if contracts are deployed
  useEffect(() => {
    const checkDeployment = async () => {
      if (!isConnected) return;
      
      try {
        const result = await checkContractsDeployed();
        setContractsDeployed(result.deployed);
        setUseMockData(!result.deployed);
        
        if (!result.deployed) {
          console.log('Contracts not deployed:', result.reason);
        }
      } catch (err) {
        console.error('Error checking contracts:', err);
        setUseMockData(true);
      }
    };
    
    checkDeployment();
  }, [isConnected, chainId, checkContractsDeployed]);

  // Fetch real data from contracts
  const fetchContractData = useCallback(async () => {
    if (!isConnected || !walletAddress || useMockData) return;
    
    try {
      const [balance, stakeInfo, stats] = await Promise.all([
        getTokenBalance(walletAddress),
        getStakeInfo(walletAddress),
        getPoolStats()
      ]);
      
      setTokenBalance(parseFloat(balance));
      setStakedAmount(parseFloat(stakeInfo.stakedAmount));
      setPendingRewards(parseFloat(stakeInfo.pendingRewards));
      setCurrentTier(stakeInfo.tier);
      setTierName(stakeInfo.tierName);
      setPoolStats({
        totalStaked: parseFloat(stats.totalStaked),
        rewardRate: stats.rewardRate,
        rewardPool: parseFloat(stats.rewardPool)
      });
    } catch (err) {
      console.error('Error fetching contract data:', err);
      // Fall back to mock data on error
      setUseMockData(true);
    }
  }, [isConnected, walletAddress, useMockData, getTokenBalance, getStakeInfo, getPoolStats]);

  // Fetch data on mount and when wallet changes
  useEffect(() => {
    if (contractsDeployed && !useMockData) {
      fetchContractData();
      // Refresh every 30 seconds
      const interval = setInterval(fetchContractData, 30000);
      return () => clearInterval(interval);
    }
  }, [contractsDeployed, useMockData, fetchContractData]);

  // Calculate current tier based on staked amount (for mock data)
  useEffect(() => {
    if (useMockData) {
      if (stakedAmount >= 500) {
        setCurrentTier(3);
        setTierName('Pro Agent');
      } else if (stakedAmount >= 200) {
        setCurrentTier(2);
        setTierName('Advanced Agent');
      } else if (stakedAmount >= 50) {
        setCurrentTier(1);
        setTierName('Basic Agent');
      } else {
        setCurrentTier(0);
        setTierName('No Agent');
      }
    }
  }, [stakedAmount, useMockData]);

  // Simulate reward accumulation for mock data
  useEffect(() => {
    if (!useMockData) return;
    
    const interval = setInterval(() => {
      if (stakedAmount > 0) {
        const rewardPerSecond = stakedAmount * 0.10 / (365 * 24 * 60 * 60);
        setPendingRewards(prev => prev + rewardPerSecond);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [stakedAmount, useMockData]);

  const handleStake = async () => {
    if (!STAKING_VERIFIED) {
      toast({
        title: 'Staking Paused',
        description: 'Staking is temporarily disabled while we verify the contract binding for the new KOJI token. Follow @KojiruHQ for updates.',
        variant: 'destructive'
      });
      return;
    }
    if (!stakeAmount || parseFloat(stakeAmount) <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }

    const amount = parseFloat(stakeAmount);
    if (amount > tokenBalance) {
      toast({ title: 'Error', description: 'Insufficient balance', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setTxHash(null);
    
    try {
      if (useMockData) {
        // Mock transaction
        await new Promise(resolve => setTimeout(resolve, 2000));
        setTokenBalance(prev => prev - amount);
        setStakedAmount(prev => prev + amount);
      } else {
        // Real contract interaction
        const result = await stakeTokens(amount);
        setTxHash(result.hash);
        await fetchContractData();
      }
      
      setStakeAmount('');
      toast({
        title: 'Staking Successful!',
        description: `You staked ${amount} KOJIRU tokens`
      });
    } catch (err) {
      toast({
        title: 'Staking Failed',
        description: err.message || 'Transaction failed',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnstake = async () => {
    if (!STAKING_VERIFIED) {
      toast({
        title: 'Staking Paused',
        description: 'Unstaking is temporarily disabled while we verify the contract binding for the new KOJI token.',
        variant: 'destructive'
      });
      return;
    }
    if (!unstakeAmount || parseFloat(unstakeAmount) <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }

    const amount = parseFloat(unstakeAmount);
    if (amount > stakedAmount) {
      toast({ title: 'Error', description: 'Insufficient staked balance', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setTxHash(null);
    
    try {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        setStakedAmount(prev => prev - amount);
        setTokenBalance(prev => prev + amount);
      } else {
        const result = await unstakeTokens(amount);
        setTxHash(result.hash);
        await fetchContractData();
      }
      
      setUnstakeAmount('');
      toast({
        title: 'Unstaking Successful!',
        description: `You unstaked ${amount} KOJIRU tokens`
      });
    } catch (err) {
      toast({
        title: 'Unstaking Failed',
        description: err.message || 'Transaction failed',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClaimRewards = async () => {
    if (!STAKING_VERIFIED) {
      toast({
        title: 'Staking Paused',
        description: 'Reward claims are temporarily disabled while we verify the contract binding for the new KOJI token.',
        variant: 'destructive'
      });
      return;
    }
    if (pendingRewards <= 0) {
      toast({ title: 'No Rewards', description: 'No rewards to claim', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setTxHash(null);
    
    try {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const claimed = pendingRewards;
        setTokenBalance(prev => prev + claimed);
        setPendingRewards(0);
      } else {
        const result = await claimRewards();
        setTxHash(result.hash);
        await fetchContractData();
      }
      
      toast({
        title: 'Rewards Claimed!',
        description: `Successfully claimed your KOJIRU rewards`
      });
    } catch (err) {
      toast({
        title: 'Claim Failed',
        description: err.message || 'Transaction failed',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const addTokenToMetaMask = async () => {
    if (!window.ethereum) {
      toast({
        title: 'MetaMask Required',
        description: 'Please install MetaMask to add the KOJIRU token',
        variant: 'destructive'
      });
      return;
    }

    try {
      const wasAdded = await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: KOJIRU_TOKEN.address || '0x0000000000000000000000000000000000000000',
            symbol: KOJIRU_TOKEN.symbol,
            decimals: KOJIRU_TOKEN.decimals,
            image: KOJIRU_TOKEN.image
          }
        }
      });

      if (wasAdded) {
        toast({
          title: 'Token Added!',
          description: 'KOJIRU token has been added to MetaMask'
        });
      }
    } catch (err) {
      toast({
        title: 'Failed to Add Token',
        description: err.message,
        variant: 'destructive'
      });
    }
  };

  const currentTierData = TIERS[currentTier];
  const nextTier = TIERS[Math.min(currentTier + 1, 3)];
  const progressToNext = currentTier < 3 
    ? Math.max(0, ((stakedAmount - currentTierData.required) / (nextTier.required - currentTierData.required)) * 100)
    : 100;

  const stakingContent = (
      <div className="max-w-6xl mx-auto space-y-6" data-testid="staking-page-root">
        {/* Maintenance Banner — shown until contract binding to new KOJI is verified */}
        {!STAKING_VERIFIED && (
          <Card className="glass border-amber-500/40 bg-amber-500/5" data-testid="staking-maintenance-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-semibold text-amber-300">
                    Staking under maintenance
                  </p>
                  <p className="text-sm text-zinc-300">
                    We&apos;re verifying the staking contract binding against the newly launched
                    KOJI token on Base mainnet. To protect user funds, stake / unstake / claim
                    actions are paused. The dashboard remains read-only. Follow{' '}
                    <a
                      href="https://twitter.com/KojiruHQ"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-amber-200"
                      data-testid="staking-maintenance-twitter-link"
                    >
                      @KojiruHQ
                    </a>{' '}
                    for the re-enable announcement.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contract Status Banner */}
        {/* Live Mode Banner */}
        {!useMockData && isConnected && contractsDeployed && (
          <Card className="glass border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div className="flex-1">
                  <p className="text-sm">
                    <span className="font-semibold text-green-500">Live on Base Mainnet</span>{' '}
                    — Connected to live staking contracts. All transactions are real.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Demo Mode Banner */}
        {useMockData && isConnected && (
          <Card className="glass border-yellow-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                <div className="flex-1">
                  <p className="text-sm">
                    <span className="font-semibold text-yellow-500">Demo Mode:</span>{' '}
                    Please connect to Base Mainnet to interact with live staking contracts.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transaction Success */}
        {txHash && (
          <Card className="glass border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div className="flex-1">
                  <p className="text-sm">
                    Transaction submitted successfully!
                  </p>
                </div>
                <a 
                  href={`${BLOCK_EXPLORER}/tx/${txHash}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  View on Explorer <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Wallet Connection Required */}
        {!isConnected && (
          <Card className="glass border-primary/30">
            <CardContent className="p-8 text-center">
              <Wallet className="w-16 h-16 mx-auto mb-4 text-primary" />
              <h2 className="text-2xl font-bold mb-2">Connect Your Wallet</h2>
              <p className="text-muted-foreground mb-6">
                Connect your wallet to stake KOJIRU tokens and access AI agents
              </p>
              <Button onClick={connectWallet} className="gradient-primary">
                Connect Wallet
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Network Warning */}
        {isConnected && !isOnBase && (
          <Card className="glass border-yellow-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Info className="w-8 h-8 text-yellow-500" />
                <div className="flex-1">
                  <h3 className="font-semibold">Wrong Network</h3>
                  <p className="text-sm text-muted-foreground">
                    Please switch to Base to interact with staking contracts
                  </p>
                </div>
                <Button onClick={switchToBase} className="bg-yellow-500 hover:bg-yellow-600">
                  Switch Network
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <Coins className="w-8 h-8 text-primary mb-2" />
              <div className="text-sm text-muted-foreground">Your Balance</div>
              <div className="text-2xl font-bold">{formatNumber(tokenBalance)} KOJI</div>
            </CardContent>
          </Card>
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <Lock className="w-8 h-8 text-green-500 mb-2" />
              <div className="text-sm text-muted-foreground">Staked</div>
              <div className="text-2xl font-bold">{formatNumber(stakedAmount)} KOJI</div>
            </CardContent>
          </Card>
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <Gift className="w-8 h-8 text-purple-500 mb-2" />
              <div className="text-sm text-muted-foreground">Pending Rewards</div>
              <div className="text-2xl font-bold">{pendingRewards.toFixed(4)} KOJI</div>
            </CardContent>
          </Card>
          <Card className="glass border-white/10">
            <CardContent className="p-6">
              <TrendingUp className="w-8 h-8 text-yellow-500 mb-2" />
              <div className="text-sm text-muted-foreground">APY</div>
              <div className="text-2xl font-bold text-green-500">{poolStats.rewardRate}%</div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3">
          <Button
            data-testid="add-token-btn"
            variant="outline"
            onClick={addTokenToMetaMask}
            className="border-white/10"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add KOJIRU to MetaMask
          </Button>
          <Link to="/staking/history">
            <Button variant="outline" className="border-white/10">
              <History className="w-4 h-4 mr-2" />
              Transaction History
            </Button>
          </Link>
          <Link to="/governance">
            <Button variant="outline" className="border-white/10">
              <Vote className="w-4 h-4 mr-2" />
              Governance
            </Button>
          </Link>
        </div>

        {/* Staking Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-green-500" />
                Stake KOJIRU
              </CardTitle>
              <CardDescription>
                Lock KOJIRU to earn rewards. No minimum, no fixed lockup.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="text-muted-foreground">Balance: {formatNumber(tokenBalance)} KOJI</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid="stake-amount-input"
                    type="number"
                    placeholder="0.00"
                    value={stakeAmount}
                    onChange={(e) => setStakeAmount(e.target.value)}
                    className="bg-white/5 border-white/10"
                    disabled={loading || contractLoading}
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => setStakeAmount(tokenBalance.toString())}
                    className="border-white/10"
                    disabled={loading || contractLoading}
                  >
                    MAX
                  </Button>
                </div>
              </div>
              <Button
                data-testid="stake-btn"
                onClick={handleStake}
                disabled={loading || contractLoading || !isConnected || !STAKING_VERIFIED}
                className="w-full gradient-primary"
              >
                {loading || contractLoading ? (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {useMockData ? 'Staking...' : 'Confirming...'}
                  </span>
                ) : (
                  'Stake KOJIRU'
                )}
              </Button>
            </CardContent>
          </Card>

          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Unlock className="w-5 h-5 text-yellow-500" />
                Unstake KOJIRU
              </CardTitle>
              <CardDescription>
                Withdraw your staked tokens (7 day minimum)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="text-muted-foreground">Staked: {formatNumber(stakedAmount)} KOJI</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid="unstake-amount-input"
                    type="number"
                    placeholder="0.00"
                    value={unstakeAmount}
                    onChange={(e) => setUnstakeAmount(e.target.value)}
                    className="bg-white/5 border-white/10"
                    disabled={loading || contractLoading}
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => setUnstakeAmount(stakedAmount.toString())}
                    className="border-white/10"
                    disabled={loading || contractLoading}
                  >
                    MAX
                  </Button>
                </div>
              </div>
              <Button
                data-testid="unstake-btn"
                onClick={handleUnstake}
                disabled={loading || contractLoading || !isConnected || stakedAmount <= 0 || !STAKING_VERIFIED}
                variant="outline"
                className="w-full border-white/10"
              >
                {loading || contractLoading ? (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {useMockData ? 'Unstaking...' : 'Confirming...'}
                  </span>
                ) : (
                  'Unstake KOJIRU'
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Claim Rewards */}
        <Card className="glass border-purple-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-purple-500/20 flex items-center justify-center">
                  <Gift className="w-7 h-7 text-purple-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Pending Rewards</h3>
                  <p className="text-3xl font-bold gradient-text">{pendingRewards.toFixed(6)} KOJI</p>
                </div>
              </div>
              <Button
                data-testid="claim-rewards-btn"
                onClick={handleClaimRewards}
                disabled={loading || contractLoading || pendingRewards <= 0 || !STAKING_VERIFIED}
                className="gradient-primary"
              >
                {loading || contractLoading ? 'Claiming...' : 'Claim Rewards'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Pool Stats */}
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle>Pool Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Total Staked</div>
                <div className="text-xl font-bold">{formatNumber(poolStats.totalStaked)} KOJI</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Reward Pool</div>
                <div className="text-xl font-bold">{formatNumber(poolStats.rewardPool)} KOJI</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Current APY</div>
                <div className="text-xl font-bold text-green-500">{poolStats.rewardRate}%</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reward Pool Health */}
        {(livePoolStats || poolStats.rewardPool > 0) && (
          <Card className="glass border-white/10" data-testid="reward-pool-health">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                Reward Pool Health
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const pool = livePoolStats ? parseFloat(livePoolStats.reward_pool) : poolStats.rewardPool;
                const staked = livePoolStats ? parseFloat(livePoolStats.total_staked) : poolStats.totalStaked;
                const distributed = livePoolStats ? parseFloat(livePoolStats.total_rewards_distributed) : 0;
                const apy = poolStats.rewardRate / 100;
                const yearlyNeeded = staked * apy;
                const yearsRunway = yearlyNeeded > 0 ? pool / yearlyNeeded : pool > 0 ? 99 : 0;
                const healthPct = Math.min(yearsRunway / 5 * 100, 100);
                const healthColor = healthPct >= 60 ? 'bg-green-500' : healthPct >= 30 ? 'bg-yellow-500' : 'bg-red-500';
                const healthLabel = healthPct >= 60 ? 'Healthy' : healthPct >= 30 ? 'Moderate' : 'Low';

                return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Pool Status</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        healthPct >= 60 ? 'bg-green-500/20 text-green-400' :
                        healthPct >= 30 ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`} data-testid="pool-health-status">
                        {healthLabel}
                      </span>
                    </div>

                    <div>
                      <div className="h-3 rounded-full bg-white/5 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${healthPct}%` }}
                          transition={{ duration: 1 }}
                          className={`h-full rounded-full ${healthColor}`}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-muted-foreground text-xs">Available Rewards</div>
                        <div className="font-semibold">{formatNumber(pool)} KOJI</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs">Distributed</div>
                        <div className="font-semibold">{formatNumber(distributed)} KOJI</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs">Est. Runway</div>
                        <div className="font-semibold">{yearsRunway > 50 ? '∞' : `${yearsRunway.toFixed(1)} years`}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs">Yearly Needed</div>
                        <div className="font-semibold">{formatNumber(yearlyNeeded)} KOJI</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t border-white/5">
                      <Activity className="w-3 h-3" />
                      <span>Live data from Base Mainnet</span>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Agent Operator bonus — informational only, not a gate */}
        <Card className="glass border-white/10 bg-gradient-to-br from-purple-500/5 to-blue-500/5">
          <CardHeader>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10 mt-1">
                <Shield className="w-5 h-5 text-purple-400" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg">Operating an AI Agent? Your stake unlocks AaaS tiers</CardTitle>
                <CardDescription className="mt-1">
                  Pure stakers earn 10% APY regardless of stake size — no thresholds, no gates. If you also operate an AI agent on Kojiru, your staked KOJI doubles as a credit-tier qualifier in the Agent Credit Protocol. Higher stakes unlock larger credit lines and richer AI tooling.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              {TIERS.slice(1).map((tier) => (
                <div
                  key={tier.id}
                  className={`p-3 rounded-lg border ${
                    currentTier >= tier.id
                      ? 'bg-primary/10 border-primary/30'
                      : 'bg-white/5 border-white/10'
                  }`}
                  data-testid={`agent-tier-card-${tier.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-sm">{tier.name}</h4>
                    <span className="text-xs text-muted-foreground">{tier.required} KOJI</span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{tier.benefits.join(' · ')}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/aaas">
                <Button variant="outline" size="sm" className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10" data-testid="learn-aaas-btn">
                  Learn about AaaS
                  <ExternalLink className="w-3 h-3 ml-2" />
                </Button>
              </Link>
              <Link to="/agent-credit">
                <Button variant="ghost" size="sm" data-testid="learn-agent-credit-btn">
                  Agent Credit Protocol
                </Button>
              </Link>
              <span className="text-xs text-muted-foreground ml-auto">Not operating an agent? Just stake above — you're done.</span>
            </div>
          </CardContent>
        </Card>
      </div>
  );

  // Use authenticated dashboard chrome when logged in, otherwise show
  // a public Navbar/Footer so anonymous wallet users can stake without an account.
  if (isAuthenticated) {
    return (
      <DashboardLayout title="KOJIRU Staking">
        {stakingContent}
      </DashboardLayout>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <main className="pt-24 pb-16 px-6">
        {stakingContent}
      </main>
      <Footer />
    </div>
  );
}
